import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// dev/app-skeleton/src/pages/EntryEditor.tsx
// 编辑器（新建 / 编辑）。
// - 路由：/entry/new （生成新 entryId）+ /entry/:id（载入）
// - 字段：title、body（纯 textarea；R2 再接 Tiptap）、mood (1..5)、attached_date
// - 自动草稿 5s（draft.ts makeDraftAutosaver，debounce 停打字 5s 才落）
// - 显式保存 → upsertEntry → 清 draft → kickSync() 推送
// - 删除：deleteEntry(墓碑) → 回 /entries
// - 全部 44×44 触控；i18n zh-CN；data-testid 留给 QA。
//
// D15 Task 2：dual UI 状态
//   - 自动保存（draftState）：dirty/saving/saved/error 单独显示
//   - 手动保存（savedAt）：保留原"已保存 · hh:mm:ss"
//   - 两条提示绝不混用 css class / data-testid
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../lib/db";
import { deleteEntry, newEntryId, upsertEntry } from "../lib/entries";
import { decryptEntryCached } from "../lib/entry-cache";
import { clearDraft, loadDraft, makeDraftAutosaver } from "../lib/draft";
import { useCurrentUserId } from "../lib/current-user";
const MOODS = [
    { v: 1, label: "低落", icon: "/mood/mood-sad.png" },
    { v: 2, label: "平淡", icon: "/mood/mood-neutral.png" },
    { v: 3, label: "平稳", icon: "/mood/mood-calm.png" },
    { v: 4, label: "积极", icon: "/mood/mood-happy.png" },
    { v: 5, label: "愉悦", icon: "/mood/mood-happy.png" },
];
function todayIso() {
    const d = new Date();
    const pad = (n) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function hhmmss(ts) {
    return new Date(ts).toLocaleTimeString("zh-CN");
}
/** 极简 tiptap-shape 包装：把纯文本塞进 {type:doc, content:[paragraph]} */
function wrapBody(text) {
    if (!text)
        return { type: "doc", content: [] };
    return {
        type: "doc",
        content: text.split(/\n{2,}/).map((p) => ({
            type: "paragraph",
            content: p ? [{ type: "text", text: p }] : [],
        })),
    };
}
/** 反向：tiptap doc → 纯文本 */
function unwrapBody(doc) {
    if (!doc || typeof doc !== "object")
        return "";
    const root = doc;
    if (!root.content)
        return "";
    return root.content
        .map((p) => (p.content ?? []).map((n) => (n.type === "text" ? n.text ?? "" : "")).join(""))
        .join("\n\n");
}
const EMPTY = { title: "", bodyText: "", mood: 3, attached_date: todayIso(), tags: [] };
/** 渲染自动保存状态。与手动保存 (savedAt) 完全分离两套 UI / testid。 */
function AutosaveBadge({ state }) {
    if (state.kind === "idle") {
        return null;
    }
    if (state.kind === "dirty") {
        return (_jsx("span", { className: "text-text-muted text-xs", "data-testid": "autosave-dirty", children: "\u5F85\u81EA\u52A8\u4FDD\u5B58\u2026" }));
    }
    if (state.kind === "saving") {
        return (_jsx("span", { className: "text-text-muted text-xs", "data-testid": "autosave-saving", "aria-live": "polite", children: "\u6B63\u5728\u81EA\u52A8\u4FDD\u5B58\u2026" }));
    }
    if (state.kind === "saved") {
        return (_jsxs("span", { className: "text-text-muted text-xs", "data-testid": "autosave-saved", children: ["\u5DF2\u81EA\u52A8\u4FDD\u5B58 ", hhmmss(state.savedAt)] }));
    }
    return (_jsxs("span", { className: "text-danger text-xs", "data-testid": "autosave-error", role: "alert", children: ["\u81EA\u52A8\u4FDD\u5B58\u5931\u8D25\uFF1A", state.message] }));
}
export function EntryEditorPage() {
    const nav = useNavigate();
    const params = useParams();
    const userId = useCurrentUserId();
    const isNew = !params.id || params.id === "new";
    const entryId = useMemo(() => (isNew ? newEntryId() : params.id), [isNew, params.id]);
    const [form, setForm] = useState(EMPTY);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [err, setErr] = useState("");
    const [draftState, setDraftState] = useState({ kind: "idle" });
    const autosaverRef = useRef(null);
    // 载入 entry + 草稿（草稿优先）
    useEffect(() => {
        if (!userId)
            return;
        let alive = true;
        (async () => {
            try {
                if (!isNew) {
                    const existing = await db.entries.get(entryId);
                    if (existing && !existing.deleted) {
                        const pt = await decryptEntryCached(userId, existing);
                        if (!alive)
                            return;
                        setForm({
                            title: pt.title,
                            bodyText: unwrapBody(pt.body_tiptap_json),
                            mood: pt.mood,
                            attached_date: pt.attached_date,
                            tags: pt.tags,
                        });
                    }
                }
                const d = await loadDraft(userId, entryId);
                if (d && alive) {
                    setForm({
                        title: d.title,
                        bodyText: unwrapBody(d.body_tiptap_json),
                        mood: d.mood,
                        attached_date: d.attached_date,
                        tags: d.tags,
                    });
                }
            }
            catch (e) {
                if (alive)
                    setErr(e instanceof Error ? e.message : "载入失败");
            }
            finally {
                if (alive)
                    setLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [userId, entryId, isNew]);
    // 草稿 5s 自动（debounce 停打字 5s 触发）+ 订阅状态
    useEffect(() => {
        if (!userId || !loaded)
            return;
        const autosaver = makeDraftAutosaver(userId, entryId, 5000);
        autosaverRef.current = autosaver;
        const unsubscribe = autosaver.onStateChange(setDraftState);
        return () => {
            unsubscribe();
            void autosaver.flush();
            autosaverRef.current = null;
        };
    }, [userId, entryId, loaded]);
    function update(k, v) {
        setForm((prev) => {
            const next = { ...prev, [k]: v };
            autosaverRef.current?.queue({
                title: next.title,
                body_tiptap_json: wrapBody(next.bodyText),
                attached_date: next.attached_date,
                tags: next.tags,
                mood: next.mood,
                attached_media_ids: [],
            });
            return next;
        });
    }
    async function onSave() {
        if (!userId)
            return;
        setSaving(true);
        setErr("");
        try {
            const pt = {
                title: form.title.trim() || "(无标题)",
                body_tiptap_json: wrapBody(form.bodyText),
                attached_date: form.attached_date,
                tags: form.tags,
                mood: form.mood,
                attached_media_ids: [],
            };
            // 先 flush 草稿（防止 5s 内 timer 还在排队却被 clearDraft 抢跑）
            await autosaverRef.current?.flush();
            await upsertEntry(userId, entryId, pt); // 内部自动 kickSync（D15 Task 1）
            await clearDraft(entryId);
            setSavedAt(Date.now());
            // 写完留在当前页（继续编辑）；UX 后续可改为返回 /entries。
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : "保存失败");
        }
        finally {
            setSaving(false);
        }
    }
    async function onDelete() {
        if (!userId || isNew)
            return;
        if (!confirm("删除这条日记？删除后可在 24h 内通过撤销快照恢复（如已做过备份/导入）。"))
            return;
        try {
            await deleteEntry(userId, entryId);
            await clearDraft(entryId);
            nav("/entries", { replace: true });
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : "删除失败");
        }
    }
    if (!userId || !loaded) {
        return (_jsx("section", { className: "px-md py-lg max-w-2xl mx-auto", "data-testid": "page-entry-editor", children: _jsx("p", { className: "text-text-muted text-sm", children: "\u8F7D\u5165\u4E2D\u2026" }) }));
    }
    return (_jsxs("section", { className: "px-md py-lg max-w-2xl mx-auto", "data-testid": "page-entry-editor", children: [_jsxs("header", { className: "mb-md flex items-center justify-between gap-sm flex-wrap", children: [_jsx("h1", { className: "text-2xl font-medium", children: isNew ? "新日记" : "编辑日记" }), _jsxs("div", { className: "flex items-center gap-md flex-wrap", children: [_jsx(AutosaveBadge, { state: draftState }), savedAt && (_jsxs("span", { className: "text-text-muted text-xs", "data-testid": "saved-at", children: ["\u5DF2\u4FDD\u5B58 \u00B7 ", hhmmss(savedAt)] }))] })] }), err && _jsx("div", { className: "text-danger text-sm mb-sm", role: "alert", "data-testid": "editor-err", children: err }), _jsxs("div", { className: "card p-md flex flex-col gap-sm", children: [_jsx("input", { type: "text", placeholder: "\u6807\u9898", value: form.title, onChange: (e) => update("title", e.target.value), className: "bg-transparent text-xl font-medium outline-none", style: { minHeight: 44 }, "data-testid": "editor-title", "aria-label": "\u6807\u9898" }), _jsxs("div", { className: "flex items-center gap-sm flex-wrap", children: [_jsx("label", { className: "text-text-muted text-sm", children: "\u65E5\u671F" }), _jsx("input", { type: "date", value: form.attached_date, onChange: (e) => update("attached_date", e.target.value), className: "bg-surface-2 px-sm py-xs rounded-md", style: { minHeight: 44 }, "data-testid": "editor-date", "aria-label": "\u65E5\u671F" })] }), _jsx("div", { className: "flex items-center gap-sm flex-wrap", role: "radiogroup", "aria-label": "\u5FC3\u60C5", children: MOODS.map((m) => (_jsxs("button", { type: "button", role: "radio", "aria-checked": form.mood === m.v, onClick: () => update("mood", m.v), className: `flex items-center gap-xs px-sm py-xs rounded-md border ${form.mood === m.v ? "border-brand bg-surface-2" : "border-border"}`, style: { minHeight: 44, minWidth: 44 }, "data-testid": `editor-mood-${m.v}`, title: m.label, children: [_jsx("img", { src: m.icon, width: 20, height: 20, alt: "", "aria-hidden": true, style: { imageRendering: "pixelated" } }), _jsx("span", { className: "text-xs", children: m.label })] }, m.v))) }), _jsx("textarea", { placeholder: "\u4ECA\u5929\u53D1\u751F\u4E86\u4EC0\u4E48\u2026", value: form.bodyText, onChange: (e) => update("bodyText", e.target.value), className: "bg-transparent outline-none resize-y min-h-[40vh] leading-relaxed", style: { fontFamily: "var(--font-serif)" }, "data-testid": "editor-body", "aria-label": "\u6B63\u6587" })] }), _jsxs("div", { className: "flex items-center gap-sm mt-md", children: [_jsx("button", { type: "button", className: "btn btn-primary", style: { minHeight: 44 }, disabled: saving, onClick: onSave, "data-testid": "btn-save", children: saving ? "保存中…" : "保存" }), _jsx("button", { type: "button", className: "btn btn-ghost", style: { minHeight: 44 }, onClick: () => nav(-1), "data-testid": "btn-back", children: "\u8FD4\u56DE" }), !isNew && (_jsx("button", { type: "button", className: "btn btn-ghost ml-auto text-danger", style: { minHeight: 44 }, onClick: onDelete, "data-testid": "btn-delete", children: "\u5220\u9664" }))] }), _jsx("p", { className: "text-text-muted text-xs mt-md", children: "\u8349\u7A3F\u6BCF 5 \u79D2\u81EA\u52A8\u52A0\u5BC6\u7F13\u5B58\u5230\u672C\u673A\uFF08\u505C\u6B62\u6253\u5B57\u540E\u89E6\u53D1\uFF09\uFF1B\u70B9\"\u4FDD\u5B58\"\u624D\u4F1A\u8FDB\u5165\u540C\u6B65\u961F\u5217\u63A8\u9001\u5230\u4E91\u7AEF\u3002" })] }));
}
